import React, { useCallback, useEffect, useState } from 'react';
import {
	Background,
	ReactFlow,
	addEdge,
	ConnectionLineType,
	Panel,
	useNodesState,
	useEdgesState,
	Position,
	Handle,
	useReactFlow,
} from '@xyflow/react';
import dagre from '@dagrejs/dagre';
import { LuAlignHorizontalJustifyStart, LuAlignVerticalJustifyEnd } from "react-icons/lu";
import '@xyflow/react/dist/style.css';
import './UnitsFlow.css';
import { BsCpu } from 'react-icons/bs';
import { Tag } from '../Tag/Tag';
import { useListManager } from '../../contexts/list.manager';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/auth';
import { VscTypeHierarchy } from 'react-icons/vsc';

const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

const nodeWidth = 185;
const nodeHeight = 70;

const UnitNode = ({ data }) => {
	return (
		<div className={`unit-node ${data.isSubOrganition ? 'border! border-(--borderRadius)!' : ''}`} style={{ width: nodeWidth + 'px', height: nodeHeight + 'px' }}>
			<h4 className="list-unit-name-collumn gap-2.5!">
				<span style={{ fontSize: '0.7rem' }}>{data.label}</span>
				<span className='inline-flex flex-row items-center gap-1!'>
					{data.isSubOrganization && (
						<span className="list-licenses-icon">
							<VscTypeHierarchy size={10} />
						</span>
					)}
					{data.licenses && data.licenses.length > 0 && (
						<span className="list-licenses-icon">
							<BsCpu size={10} />
						</span>
					)}
				</span>
			</h4>
			{data.tags && data.tags.length > 0 && (
				<div style={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
					{data.tags?.map(tag => <Tag key={tag.id} tag={tag} withoutBorders={true} />)}
				</div>
			)}
			<Handle type="source" position={Position.Bottom} />
			<Handle type="target" position={Position.Top} />
		</div>
	);
}

const getLayoutedElements = (nodes, edges, direction = 'TB') => {
	const isHorizontal = direction === 'LR';
	dagreGraph.setGraph({ rankdir: direction });

	nodes.forEach((node) => {
		dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
	});

	edges.forEach((edge) => {
		dagreGraph.setEdge(edge.source, edge.target);
	});

	dagre.layout(dagreGraph);

	const newNodes = nodes.map((node) => {
		const nodeWithPosition = dagreGraph.node(node.id);
		const newNode = {
			...node,
			targetPosition: isHorizontal ? 'left' : 'top',
			sourcePosition: isHorizontal ? 'right' : 'bottom',
			position: {
				x: nodeWithPosition.x - nodeWidth / 2,
				y: nodeWithPosition.y - nodeHeight / 2,
			},
		};

		return newNode;
	});

	return { nodes: newNodes, edges };
};

export const UnitsFlow = ({ units }) => {
	const { user } = useAuth();
	const { handleLineClick } = useListManager();
	const navigate = useNavigate();

	const initialEdges = units.reduce((acc, unit) => {
		if (unit.parent) {
			acc.push({
				id: `${unit.parent.id}-${unit.id}`,
				source: unit.parent.parentId === null ? 'root' : unit.parent.id,
				target: unit.id,
				type: "smoothstep",
				animated: true,
			});
		}
		return acc;
	}, []);

	const hasRootConnection = units.find(unit => unit.parent.hierarchyPath === 'root');

	const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
		[
			hasRootConnection ? {
				id: 'root',
				data: { label: user.userData.selectedOrganization?.name ?? 'Root', root: true },
				type: 'unit',
				position: { x: 0, y: 0 },
			} : {},
			...units.map((unit, idx) => ({
				id: unit.id,
				data: { idx, label: unit.name, ...unit },
				type: 'unit',
				position: { x: 0, y: 0 },
			})),
		],
		initialEdges,
	);

	const { fitView } = useReactFlow();
	const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
	const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);

	const onConnect = useCallback(
		(params) =>
			setEdges((eds) =>
				addEdge({ ...params, type: ConnectionLineType.SmoothStep, animated: true }, eds),
			),
		[],
	);
	const onLayout = useCallback(
		(direction) => {
			const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
				nodes,
				edges,
				direction,
			);

			setNodes([...layoutedNodes]);
			setEdges([...layoutedEdges]);
		},
		[nodes, edges],
	);

	return (
		<ReactFlow
			nodes={nodes}
			edges={edges}
			nodeTypes={{ unit: UnitNode }}
			onNodesChange={onNodesChange}
			onEdgesChange={onEdgesChange}
			onNodeClick={(_, node) => {
				if (node.data?.root) return;
				handleLineClick(node.data, node.data.idx);
				fitView({
					nodes: [{ id: node.id }],
					duration: 500,
				})
			}}
			onConnectEnd={(_, state) => {
				navigate('../cadastro-condominios', { state: { parentUnitId: state.fromNode.data.id } });
			}}
			connectionLineType={ConnectionLineType.SmoothStep}
			fitView
		>
			<Panel position="top-right">
				<div className='flow-direction-buttons'>
					<button className="xy-theme__button" onClick={() => onLayout('TB')}>
						<LuAlignVerticalJustifyEnd />
					</button>
					<button className="xy-theme__button" onClick={() => onLayout('LR')}>
						<LuAlignHorizontalJustifyStart />
					</button>
				</div>
			</Panel>
			<Background />
		</ReactFlow>
	);
};
